import { buildStyleUrl, buildDirectoryUrl } from './slugs';
import { DEFAULT_STATE } from './stateCore.mjs';
import type { StateSlug } from './stateCore.mjs';
import { resolveBlogRegion } from '../src/constants/blogTags';
import { DEFAULT_STATE_HUB_PATH } from './stateRoutes';

/**
 * Blog → directory-hub linking (items 4 + 5). Pure + firebase-free (mirrors
 * lib/readTime.ts / lib/relatedPosts.ts), so the hub-aware CTA (item 5) and the inline-link
 * authoring convention (item 4) share ONE canonical URL-shape source.
 */

/**
 * State-level "floor" for a suburb-less style post. buildStyleUrl ALWAYS inserts a suburb
 * segment (a missing suburb becomes the literal 'nsw' → a doubled `/nsw/nsw/gyms/...`), and
 * the proper suburb-less `/nsw/gyms/:style` route does NOT exist (item 48, Bucket 7). So a
 * style-only post links to the state gyms hub rather than a fabricated suburb.
 */
// Derived, not literal: this IS the default state's hub, and DEFAULT_STATE_HUB_PATH
// (lib/stateRoutes.ts, via buildStateHubUrl) is its single source. While DEFAULT_STATE
// is 'nsw' the value is '/nsw/suburbs' since UI-57. The exported NAME is kept because
// lib/blogHubLinks.test.ts imports it.
export const NSW_GYMS_FLOOR = DEFAULT_STATE_HUB_PATH;

/**
 * Append the directory's LIVE `?age=` filter (the navigational age-group axis) to a base
 * hub path, using the EXACT param name + value encoding the directory round-trips:
 * hooks/useDirectoryFilters parses `searchParams.get('age').split(',')`, and DirectoryPage
 * writes `params.set('age', ageGroups.join(','))` via URLSearchParams. No-op when there are
 * no age groups — so a post without an age group yields the same path as before.
 */
const withAgeQuery = (base: string, ageGroups?: readonly string[] | undefined): string => {
  if (!ageGroups || ageGroups.length === 0) return base;
  const query = new URLSearchParams({ age: ageGroups.join(',') }).toString();
  return `${base}?${query}`;
};

/**
 * Canonical directory-hub href for a (style, suburb) pair — the item-4 inline-link primitive
 * AND the URL-shape source the item-5 CTA reuses. Handles the suburb constraint so callers
 * never emit a doubled-`nsw` path:
 *   - style + suburb → /nsw/{suburb}/gyms/{style}   (buildStyleUrl)
 *   - style only     → /gyms?styles=…               (the chooser carries the style onward — UI-57)
 *   - suburb only    → /nsw/{suburb}/gyms           (buildDirectoryUrl)
 *   - neither        → /gyms                         (generic directory)
 * `style` is a directory style DISPLAY name (e.g. "Muay Thai"); `suburb` is a hub-location
 * slug or display name (both slugify identically via the builders). An optional `ageGroups`
 * appends the directory's `?age=` filter to whatever base path the above produces.
 */
export const hubHref = ({
  style,
  suburb,
  ageGroups,
}: {
  style?: string | undefined;
  suburb?: string | undefined;
  ageGroups?: readonly string[] | undefined;
}): string => {
  // UI-56 (operator-ruled 2026-08-11) — RESOLVE THE REGION'S STATE. Every branch below
  // used to hardcode DEFAULT_STATE and this module imported nothing from the tag
  // vocabulary, so it could never learn that a region tag names a different state.
  // Proven by executing the real builders: hubHref({ suburb: 'vic-richmond' }) returned
  // '/nsw/richmond-2753/gyms' — byte-identical to the NSW suburb of the same name. That
  // is the UI-01 wrong-state family, and it was ARMED rather than live only because no
  // published post carries a state-qualified tag yet. Carrying tags into the baked page
  // (the Data & Pipeline half of this item) is exactly what would have fired it, which
  // is why this half goes FIRST.
  //
  // resolveBlogRegion accepts BOTH forms and is the single source for the mapping: a
  // bare slug keeps its historical DEFAULT_STATE meaning, a '<state>-<slug>' tag
  // resolves to that state.
  //
  // ⚠️ IT RETURNS null FOR ANYTHING OUTSIDE THE TAG VOCABULARY, AND THE FALLBACK MUST BE
  // THE OLD BEHAVIOUR, NOT A DROP. My first version dropped an unresolved region on the
  // reasoning that an unknown suburb should not silently become an NSW URL — which broke
  // two things this function has always supported and which its own docblock promises:
  // a suburb DISPLAY name (the builders slugify it) and any hub location not in the blog
  // tag vocabulary. Five existing assertions caught it. The correct posture is ADDITIVE:
  // resolve the state when the vocabulary can tell us, otherwise behave exactly as before.
  const resolvedRegion = suburb ? resolveBlogRegion(suburb) : null;
  const regionState = (resolvedRegion?.state as StateSlug | undefined) ?? DEFAULT_STATE;
  const regionSlug = resolvedRegion?.slug ?? suburb;

  let base: string;
  if (style && regionSlug) base = buildStyleUrl(regionSlug, style, regionState);
  else if (style) {
    // ITEM 6 (operator-ruled 2026-08-12, both lanes) — a STYLE-ONLY article now goes to
    // the FILTERED DIRECTORY with its style pre-ticked, not to the state floor.
    //
    // Why the floor was wrong: the floor was then the 601-suburb A–Z INDEX, not the
    // directory. It does not mount the filter hook, so `?age=` was inert there and the
    // article's style — the one thing the reader had just spent five minutes on — was
    // discarded entirely. UI-53 made a filtered directory address a legitimate,
    // shareable destination, which is what makes this coherent now and did not before.
    //
    // ⚠️ THE ACCEPTED COST, recorded so nobody reads the empty list as a bug: the
    // directory returns NOTHING until a location is chosen, so the reader lands with
    // the style ticked and a prompt to pick a suburb. Ruled better than the index,
    // which silently drops the style.
    //
    // ⚠️ triggerFilters HISTORY, kept honest through two flips: at the original hub
    // destination the flag was INERT (overlay aria-hidden there — I once reported the
    // opposite; D&P proved it from the built artefact). Between 2026-08-12 and the
    // UI-57 flip, /gyms WAS directory space and the flag fired. Since UI-57, /gyms is
    // the CHOOSER — the flag is inert again on this target (documented at its consumer
    // in DirectoryPage) and the style survives via the chooser pills' carried params,
    // which is the operator-ruled landing (+1 click, nothing dropped).
    //
    // Built as ONE query rather than via withAgeQuery, which appends a bare `?` and
    // would emit `/gyms?styles=BJJ?age=…` on a base that already carries a query.
    // Param names match what the directory WRITES and READS (styles, age).
    const params = new URLSearchParams();
    params.set('styles', style);
    if (ageGroups && ageGroups.length > 0) params.set('age', ageGroups.join(','));
    return `/gyms?${params.toString()}`;
  }
  else if (regionSlug) base = buildDirectoryUrl(regionSlug, regionState);
  else base = '/gyms';
  return withAgeQuery(base, ageGroups);
};

// Minimal shape the CTA derivation needs — a HydratedBlogPost satisfies it, and tests can
// pass plain string arrays (no vocab-literal casts).
type BlogCtaSource = {
  styles?: readonly string[] | undefined;
  regions?: readonly string[] | undefined;
  ageGroups?: readonly string[] | undefined;
};

/**
 * Derive the hub-aware BlogCtaCard target from a post's PRIMARY faceted tag (item 5).
 * Primary style (styles[0]) wins over primary region (regions[0]), per the item-10 model.
 *
 * ⚠️ THE PREVIOUS DOCBLOCK HERE WAS FALSE AND IS CORRECTED (UI-56, 2026-08-11). It said
 * "a post with no tags — every current seed post", and concluded the CTA was "safe to ship
 * now against the untagged corpus with zero visible change until posts carry tags". A
 * read-only production query found ALL 13 published posts already carry topic tags. The
 * corpus was never untagged; only the BAKED PAYLOAD is, because the page-building step
 * does not copy the tags through (Data & Pipeline's half of this item). The observable
 * consequence is that this CTA is not inert but INCONSISTENT: a reader arriving by
 * client-side navigation from the blog index gets the smart target, while the same URL
 * loaded directly gets `/gyms`. A comment asserting the corpus is untagged is exactly the
 * false-zero generator this programme keeps cataloguing — it would have told the next
 * verifier not to bother looking.
 *
 * GRACEFUL DEGRADATION still holds for the genuinely tagless case: no post at all (the
 * index/category pages pass none) returns `/gyms` + { triggerFilters: true }.
 */
export const getBlogCtaTarget = (
  post?: BlogCtaSource
): { href: string; triggerFilters: boolean } => {
  const style = post?.styles?.[0];
  const suburb = post?.regions?.[0];
  // age-group is a navigational decoration on whatever base the style/region priority yields;
  // triggerFilters stays governed SOLELY by the no-style-no-region (generic) case, so a post
  // with NO age group is byte-identical to the pre-age mechanism.
  const href = hubHref({ style, suburb, ageGroups: post?.ageGroups });
  // UI-56: triggerFilters asks "did we land somewhere that still needs the visitor to
  // choose a location?" — which is governed by the absence of a SUBURB, not by the
  // absence of any tag. Keyed on `!style && !suburb` it flipped false for a style-only
  // post, so a reader landing on the state floor got no filter panel and no style
  // applied: strictly worse than the generic directory they used to get. Deriving it
  // from the suburb keeps the generic case byte-identical (no style, no suburb → true)
  // while making a style-only target actually usable.
  return { href, triggerFilters: !suburb };
};
